import { useCallback, useMemo, useState, type FormEvent } from 'react'
import {
  leadBuyerTypes,
  leadContactMethods,
  leadFrequencies,
  leadProducts,
  leadQuantityRanges,
  leadRequestPolicy,
  validateLeadRequest,
} from '../application/leadRequestPolicy'
import type {
  LeadFrequency,
  LeadRequestDraft,
} from '../domain/leadRequest.types'
import { submitLeadRequest } from '../infrastructure/leadRequestClient'
import { TurnstileWidget } from './TurnstileWidget'

const TOTAL_STEPS = 5

type PurchaseMode = 'one-time' | 'recurrent' | 'evaluating' | ''

const initialDraft: LeadRequestDraft = {
  buyerType: '',
  contactName: '',
  companyName: '',
  product: '',
  quantityRange: '',
  destination: '',
  frequency: '',
  contactMethod: '',
  email: '',
  whatsapp: '',
  website: '',
}

function labelFor<T extends string>(items: readonly Readonly<{ value: T; label: string }>[], value: T | '') {
  return items.find((item) => item.value === value)?.label ?? ''
}

function frequencySummary(frequency: LeadRequestDraft['frequency']) {
  if (frequency === 'one-time') return 'Compra puntual'
  if (frequency === 'evaluating') return 'Por definir'
  return labelFor(leadFrequencies, frequency)
}

export function LeadRequestSection() {
  const [draft, setDraft] = useState<LeadRequestDraft>(initialDraft)
  const [step, setStep] = useState(0)
  const [purchaseMode, setPurchaseMode] = useState<PurchaseMode>('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [resetNonce, setResetNonce] = useState(0)
  const [notice, setNotice] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? ''
  const validation = useMemo(() => validateLeadRequest(draft), [draft])
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), [])

  const productLabel = labelFor(leadProducts, draft.product)
  const quantityLabel = labelFor(leadQuantityRanges, draft.quantityRange)
  const purchaseLabel = frequencySummary(draft.frequency)

  function updateDraft(patch: Partial<LeadRequestDraft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setNotice(null)
  }

  function advance(nextStep = step + 1) {
    setNotice(null)
    setStep(Math.min(nextStep, TOTAL_STEPS - 1))
  }

  function goBack() {
    if (step === TOTAL_STEPS - 1) {
      setTurnstileToken('')
      setResetNonce((value) => value + 1)
    }
    setNotice(null)
    setStep((current) => Math.max(0, current - 1))
  }

  function selectPurchaseMode(mode: Exclude<PurchaseMode, ''>) {
    setPurchaseMode(mode)
    if (mode === 'one-time') {
      updateDraft({ frequency: 'one-time' })
      advance()
      return
    }
    if (mode === 'evaluating') {
      updateDraft({ frequency: 'evaluating' })
      advance()
      return
    }
    updateDraft({ frequency: '' })
  }

  function selectRecurringFrequency(frequency: LeadFrequency) {
    updateDraft({ frequency })
    advance()
  }

  function continueDestination() {
    if (draft.destination.trim().length < 2) {
      setNotice('Indica una ciudad, estado o código postal aproximado.')
      return
    }
    advance()
  }

  function resetFlow() {
    setDraft(initialDraft)
    setStep(0)
    setPurchaseMode('')
    setTurnstileToken('')
    setResetNonce((value) => value + 1)
    setNotice(null)
    setStatus('idle')
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const currentValidation = validateLeadRequest(draft)
    if (!currentValidation.isValid || !currentValidation.request) {
      setNotice(currentValidation.message)
      return
    }
    if (!siteKey || !turnstileToken) {
      setNotice('Completa la verificación para enviar tu propuesta.')
      return
    }

    setStatus('sending')
    setNotice(null)
    try {
      await submitLeadRequest(currentValidation.request, turnstileToken, draft.website)
      setStatus('sent')
      setNotice(null)
    } catch (error) {
      setStatus('idle')
      setNotice(error instanceof Error ? error.message : 'No pudimos enviar tu solicitud. Inténtalo de nuevo.')
      setResetNonce((value) => value + 1)
      setTurnstileToken('')
    }
  }

  const progress = status === 'sent' ? 100 : ((step + 1) / TOTAL_STEPS) * 100

  return (
    <section className="section quote-flow" aria-labelledby="lead-request-title" id="cotizar">
      <div className="quote-flow__intro">
        <p>COTIZAR</p>
        <h2 id="lead-request-title">Hablemos de lo que necesitas.</h2>
        <span>Empieza por lo esencial. Una decisión a la vez; nosotros hacemos el resto.</span>
      </div>

      <form className="quote-flow__shell" onSubmit={submit} noValidate>
        <div
          className="quote-flow__progress"
          role="progressbar"
          aria-label="Avance de la cotización"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <span style={{ width: `${progress}%` }} />
        </div>

        {status === 'sent' ? (
          <div className="quote-flow__success" role="status">
            <p>PROPUESTA SOLICITADA</p>
            <h3>Ya tenemos lo importante.</h3>
            <span>Recibimos tu solicitud comercial. Te contactaremos por el canal que elegiste para revisar disponibilidad, precio y condiciones de entrega.</span>
            <button type="button" className="quote-flow__primary-action" onClick={resetFlow}>Hacer otra solicitud</button>
          </div>
        ) : (
          <>
            <div className="quote-flow__body" key={step}>
              <aside className="quote-flow__context" aria-label="Resumen de la cotización">
                <p>PROPUESTA COMERCIAL</p>
                <dl>
                  {productLabel && <div><dt>Producto</dt><dd>{productLabel}</dd></div>}
                  {quantityLabel && <div><dt>Volumen</dt><dd>{quantityLabel}</dd></div>}
                  {purchaseLabel && <div><dt>Compra</dt><dd>{purchaseLabel}</dd></div>}
                  {draft.destination.trim() && <div><dt>Destino</dt><dd>{draft.destination.trim()}</dd></div>}
                </dl>
              </aside>

              <div className="quote-flow__decision">
                {step === 0 && (
                  <div className="quote-flow__step">
                    <p className="quote-flow__eyebrow">PRODUCTO</p>
                    <h3>¿Qué necesitas?</h3>
                    <div className="quote-flow__options">
                      {leadProducts.map((item) => (
                        <button
                          type="button"
                          key={item.value}
                          className={draft.product === item.value ? 'quote-flow__option is-selected' : 'quote-flow__option'}
                          onClick={() => {
                            updateDraft({ product: item.value })
                            advance(1)
                          }}
                        >
                          <strong>{item.label}</strong>
                          <span>{item.note}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="quote-flow__step">
                    <p className="quote-flow__eyebrow">VOLUMEN</p>
                    <h3>¿Qué volumen tienes en mente?</h3>
                    <div className="quote-flow__options quote-flow__options--quantity">
                      {leadQuantityRanges.map((item) => (
                        <button
                          type="button"
                          key={item.value}
                          className={draft.quantityRange === item.value ? 'quote-flow__option is-selected' : 'quote-flow__option'}
                          onClick={() => {
                            updateDraft({ quantityRange: item.value })
                            advance(2)
                          }}
                        >
                          <strong>{item.label}</strong>
                          <span>{item.note}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="quote-flow__step">
                    <p className="quote-flow__eyebrow">MODALIDAD</p>
                    <h3>¿Cómo lo necesitas?</h3>
                    <div className="quote-flow__options quote-flow__options--mode">
                      <button type="button" className={purchaseMode === 'one-time' ? 'quote-flow__option is-selected' : 'quote-flow__option'} onClick={() => selectPurchaseMode('one-time')}>
                        <strong>Compra puntual</strong>
                        <span>Una necesidad específica de producto y volumen.</span>
                      </button>
                      <button type="button" className={purchaseMode === 'recurrent' ? 'quote-flow__option is-selected' : 'quote-flow__option'} onClick={() => selectPurchaseMode('recurrent')}>
                        <strong>Suministro recurrente</strong>
                        <span>Continuidad y planeación alrededor de tu consumo.</span>
                      </button>
                      <button type="button" className={purchaseMode === 'evaluating' ? 'quote-flow__option is-selected' : 'quote-flow__option'} onClick={() => selectPurchaseMode('evaluating')}>
                        <strong>Todavía lo estoy evaluando</strong>
                        <span>Podemos ayudarte a aterrizar la necesidad.</span>
                      </button>
                    </div>

                    {purchaseMode === 'recurrent' && (
                      <div className="quote-flow__cadence">
                        <p>¿Con qué frecuencia?</p>
                        <div>
                          {leadFrequencies
                            .filter((item) => ['weekly', 'biweekly', 'monthly', 'recurrent-flexible'].includes(item.value))
                            .map((item) => (
                              <button type="button" key={item.value} onClick={() => selectRecurringFrequency(item.value)}>
                                {item.label}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 3 && (
                  <div className="quote-flow__step quote-flow__step--field">
                    <p className="quote-flow__eyebrow">DESTINO</p>
                    <h3>¿A dónde lo enviaríamos?</h3>
                    <label className="quote-flow__field">
                      <span>Ciudad, estado o C.P. aproximado</span>
                      <input
                        type="text"
                        maxLength={leadRequestPolicy.maxDestinationLength}
                        value={draft.destination}
                        onChange={(event) => updateDraft({ destination: event.target.value })}
                        placeholder="Ej. León, Guanajuato"
                        autoComplete="address-level2"
                        autoFocus
                        required
                      />
                      <small>No necesitamos tu dirección completa en este momento.</small>
                    </label>
                    <button type="button" className="quote-flow__primary-action" onClick={continueDestination}>Continuar</button>
                  </div>
                )}

                {step === 4 && (
                  <div className="quote-flow__step quote-flow__step--contact">
                    <p className="quote-flow__eyebrow">CONTACTO</p>
                    <h3>¿Dónde te contactamos?</h3>

                    <fieldset className="quote-flow__choice-group">
                      <legend>¿Quién realiza la compra?</legend>
                      <div>
                        {leadBuyerTypes.map((item) => (
                          <button
                            type="button"
                            key={item.value}
                            className={draft.buyerType === item.value ? 'is-selected' : ''}
                            onClick={() => updateDraft({ buyerType: item.value, companyName: item.value === 'business' ? draft.companyName : '' })}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </fieldset>

                    <div className="quote-flow__contact-fields">
                      <label className="quote-flow__field">
                        <span>Nombre</span>
                        <input
                          type="text"
                          maxLength={leadRequestPolicy.maxContactNameLength}
                          value={draft.contactName}
                          onChange={(event) => updateDraft({ contactName: event.target.value })}
                          placeholder="Nombre y apellido"
                          autoComplete="name"
                          required
                        />
                      </label>

                      {draft.buyerType === 'business' && (
                        <label className="quote-flow__field">
                          <span>Empresa</span>
                          <input
                            type="text"
                            maxLength={leadRequestPolicy.maxCompanyNameLength}
                            value={draft.companyName}
                            onChange={(event) => updateDraft({ companyName: event.target.value })}
                            placeholder="Nombre de la empresa"
                            autoComplete="organization"
                            required
                          />
                        </label>
                      )}
                    </div>

                    <fieldset className="quote-flow__choice-group">
                      <legend>¿Cómo prefieres que te contactemos?</legend>
                      <div>
                        {leadContactMethods.map((item) => (
                          <button
                            type="button"
                            key={item.value}
                            className={draft.contactMethod === item.value ? 'is-selected' : ''}
                            onClick={() => updateDraft({
                              contactMethod: item.value,
                              email: item.value === 'email' ? draft.email : '',
                              whatsapp: item.value === 'whatsapp' ? draft.whatsapp : '',
                            })}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </fieldset>

                    {draft.contactMethod === 'email' && (
                      <label className="quote-flow__field">
                        <span>Correo</span>
                        <input
                          type="email"
                          maxLength={leadRequestPolicy.maxEmailLength}
                          value={draft.email}
                          onChange={(event) => updateDraft({ email: event.target.value })}
                          placeholder="tu@correo.com"
                          autoComplete="email"
                          required
                        />
                      </label>
                    )}

                    {draft.contactMethod === 'whatsapp' && (
                      <label className="quote-flow__field">
                        <span>WhatsApp</span>
                        <input
                          type="tel"
                          maxLength={leadRequestPolicy.maxWhatsappLength}
                          value={draft.whatsapp}
                          onChange={(event) => updateDraft({ whatsapp: event.target.value })}
                          placeholder="+52 771 000 0000"
                          autoComplete="tel"
                          inputMode="tel"
                          required
                        />
                      </label>
                    )}

                    <label className="quote-flow__honeypot" aria-hidden="true">
                      <span>Sitio web</span>
                      <input type="text" tabIndex={-1} autoComplete="off" value={draft.website} onChange={(event) => updateDraft({ website: event.target.value })} />
                    </label>

                    {siteKey && <TurnstileWidget siteKey={siteKey} onTokenChange={handleTurnstileToken} resetNonce={resetNonce} />}

                    <button type="submit" className="quote-flow__primary-action" disabled={status === 'sending' || !validation.isValid}>
                      {status === 'sending' ? 'Enviando…' : 'Solicitar propuesta'}
                    </button>
                  </div>
                )}

                {notice && <p className="quote-flow__notice" role="alert">{notice}</p>}
              </div>
            </div>

            <div className="quote-flow__footer">
              {step > 0 ? <button type="button" className="quote-flow__back" onClick={goBack}>← Volver</button> : <span />}
              <p>Sin compromiso. Revisamos disponibilidad, logística y condiciones antes de contactarte.</p>
            </div>
          </>
        )}
      </form>
    </section>
  )
}
